import { promises as fs } from 'node:fs';
import path from 'node:path';

/**
 * Auto-wire Loupe into a host project's entry file.
 *
 * Right now: Next.js App Router only (the most-requested target).
 * Other frameworks fall back to the tailored paste-it-yourself
 * flow handled by `init.ts`.
 *
 * The approach is intentionally surgical:
 *   1. Create a new sibling `loupe-provider.tsx` that owns the
 *      client-side Loupe JSX. This is ALWAYS a new file — can't
 *      clash with anything.
 *   2. Back up the entry file (layout.tsx → layout.tsx.loupe-backup)
 *      before touching it.
 *   3. Insert a single import line + wrap the first `{children}` in
 *      the file with `<LoupeProvider>{children}</LoupeProvider>`.
 *      Simple string edit — no full AST transform. If the file
 *      looks non-standard (no `{children}` marker, already wired,
 *      etc.) we bail and return null so init falls back to manual.
 *
 * `uninstall.ts` knows how to undo this: restore from the backup
 * and delete the provider file.
 */

export type AutoWireResult = {
  /** Relative path of the entry file we modified. */
  entryFile: string;
  /** Relative path of the backup file we wrote. */
  backupFile: string;
  /** Relative path of the new provider file we created. */
  providerFile: string;
  /** Relative path of the demo-scene file we created. */
  demoFile: string;
};

export async function autoWireNextjs(cwd: string): Promise<AutoWireResult | null> {
  const candidates = [
    'app/layout.tsx',
    'src/app/layout.tsx',
    'app/layout.jsx',
    'src/app/layout.jsx',
  ];

  let entryRel: string | null = null;
  for (const rel of candidates) {
    try {
      await fs.access(path.join(cwd, rel));
      entryRel = rel;
      break;
    } catch {
      /* keep looking */
    }
  }
  if (!entryRel) return null;

  const entryAbs = path.join(cwd, entryRel);
  const original = await fs.readFile(entryAbs, 'utf8');

  // Already wired — don't touch again.
  if (
    original.includes('@arinze-clinton/loupe') ||
    original.includes('./loupe-provider') ||
    original.includes('loupe-provider\'') ||
    original.includes('loupe-provider"')
  ) {
    return null;
  }

  // Standard Next layout shape requires `{children}` somewhere.
  // If it's not there, this is a custom layout we shouldn't touch.
  if (!original.includes('{children}')) return null;

  const entryDir = path.dirname(entryRel);
  const providerRel = path.join(entryDir, 'loupe-provider.tsx');
  const providerAbs = path.join(cwd, providerRel);
  const demoRel = path.join(entryDir, 'loupe-demo-scene.tsx');
  const demoAbs = path.join(cwd, demoRel);
  const backupRel = `${entryRel}.loupe-backup`;
  const backupAbs = path.join(cwd, backupRel);

  // 1a. Write the demo-scene file so first-run users see Loupe
  //     working immediately without having to wire their own scenes.
  //     User can replace it with their real scenes later, or delete
  //     the file + the import from loupe-provider.tsx.
  await fs.writeFile(demoAbs, NEXTJS_DEMO_SCENE_TEMPLATE, 'utf8');

  // 1b. Write the client provider file — imports the demo by default
  //     so Loupe has a scene to drive on first run.
  await fs.writeFile(providerAbs, NEXTJS_PROVIDER_TEMPLATE, 'utf8');

  // 2. Back up the entry file.
  await fs.writeFile(backupAbs, original, 'utf8');

  // 3. Rewrite the entry: insert import + wrap children.
  const importLine = `import { LoupeProvider } from './loupe-provider';`;
  const withImport = insertAfterLastImport(original, importLine);
  // Only wrap the FIRST occurrence — the Next.js layout pattern
  // has one `{children}`. Safer than replace-all.
  const withWrap = withImport.replace(
    '{children}',
    '<LoupeProvider>{children}</LoupeProvider>',
  );

  await fs.writeFile(entryAbs, withWrap, 'utf8');

  return {
    entryFile: entryRel,
    backupFile: backupRel,
    providerFile: providerRel,
    demoFile: demoRel,
  };
}

/**
 * Find the last top-level import statement and insert `line` on its
 * own line right after. Falls back to prepending if no imports exist.
 */
/**
 * If the host already has `loupe-demo-scene.tsx` from a prior init,
 * silently upgrade it to the latest template — BUT only if we're
 * confident we generated it in the first place. We detect Loupe-
 * generated copies two ways:
 *
 *   1. The file contains the v2 marker (introduced in 0.2.15).
 *   2. The file contains phrases unique to the 0.2.8–0.2.14 templates
 *      ("Loupe demo scene — shows Loupe is working on first install.")
 *      so first-time upgraders get picked up too.
 *
 * Any file that matches neither is assumed to be user-edited and
 * left alone. Returns the relative path(s) we upgraded.
 */
export async function upgradeDemoSceneIfGenerated(
  cwd: string,
  opts: { backup?: boolean } = {},
): Promise<string[]> {
  const candidates = [
    'app/loupe-demo-scene.tsx',
    'src/app/loupe-demo-scene.tsx',
  ];
  const upgraded: string[] = [];
  for (const rel of candidates) {
    const abs = path.join(cwd, rel);
    let src: string;
    try {
      src = await fs.readFile(abs, 'utf8');
    } catch {
      continue;
    }
    const isGenerated =
      src.includes(LOUPE_DEMO_GENERATED_MARKER) ||
      // Backwards-compat fingerprint from earlier templates (v0.2.8+).
      src.includes('Loupe demo scene — shows Loupe is working on first install');
    if (!isGenerated) continue;
    // Already on the latest template — no-op.
    if (src.includes(LOUPE_DEMO_GENERATED_MARKER) && src.includes('useOptionalLoupeRegistry')) {
      continue;
    }
    if (opts.backup) {
      await fs.writeFile(`${abs}.loupe-backup`, src, 'utf8');
    }
    await fs.writeFile(abs, NEXTJS_DEMO_SCENE_TEMPLATE, 'utf8');
    upgraded.push(rel);
  }
  return upgraded;
}

function insertAfterLastImport(source: string, line: string): string {
  // Match a run of top-level imports at the start of lines.
  const importRegex = /^import\s[^\n]*;$/gm;
  let last: RegExpExecArray | null = null;
  let match: RegExpExecArray | null;
  while ((match = importRegex.exec(source)) !== null) {
    last = match;
  }
  if (!last) {
    return `${line}\n\n${source}`;
  }
  const end = last.index + last[0].length;
  return `${source.slice(0, end)}\n${line}${source.slice(end)}`;
}

const NEXTJS_PROVIDER_TEMPLATE = `'use client';

// Loupe — timeline-first motion authoring tool.
// Generated by \`loupe init\`. Safe to edit; \`loupe uninstall\` will
// remove this file + the import added to layout.tsx.

import {
  LoupeRegistryProvider,
  LoupePanel,
  AnnotationsProvider,
  AnnotationOverlay,
  AnnotationPins,
} from '@arinze-clinton/loupe';
// Demo scene so Loupe has something to drive on first run.
// Replace with your real scenes, or delete both lines when done.
import { LoupeDemoScene } from './loupe-demo-scene';

const isDev = process.env.NODE_ENV !== 'production';

export function LoupeProvider({ children }: { children: React.ReactNode }) {
  return (
    <LoupeRegistryProvider>
      <AnnotationsProvider>
        {children}
        {isDev && (
          <>
            {/* Delete <LoupeDemoScene /> once you've wired your own scenes. */}
            <LoupeDemoScene />
            <LoupePanel />
            <AnnotationOverlay />
            <AnnotationPins />
          </>
        )}
      </AnnotationsProvider>
    </LoupeRegistryProvider>
  );
}
`;

/** Marker embedded in the generated demo scene so `loupe init`
 *  can detect it and silently upgrade it to the latest template
 *  without overwriting files the user has edited. */
export const LOUPE_DEMO_GENERATED_MARKER = 'LOUPE_GENERATED:demo-scene:v2';

const NEXTJS_DEMO_SCENE_TEMPLATE = `'use client';

// ${LOUPE_DEMO_GENERATED_MARKER}
//
// Loupe demo scene — shows you how Loupe works on first install.
// The dot only appears while "Demo" is the active scene in the Loupe
// panel; once your own scenes are registered, Loupe auto-switches
// away from the demo and this file stays silent until you pick
// "Demo" from the scene dropdown again.
//
// Delete this file + the \`<LoupeDemoScene />\` line in
// \`loupe-provider.tsx\` when you're done learning — \`loupe uninstall\`
// does that for you automatically.

import {
  useTimelineValue,
  TimelineProvider,
  useOptionalLoupeRegistry,
} from '@arinze-clinton/loupe';

const DEMO_CONFIG = {
  id: 'loupe-demo',
  label: 'Demo',
  phaseOrder: ['idle', 'enter', 'hold', 'exit'] as const,
  phaseDurations: { idle: 400, enter: 700, hold: 900, exit: 600 },
};

export function LoupeDemoScene() {
  return (
    <TimelineProvider config={DEMO_CONFIG}>
      <DemoDot />
    </TimelineProvider>
  );
}

function DemoDot() {
  // Hooks run unconditionally — then we decide whether to paint.
  const registry = useOptionalLoupeRegistry();
  const x = useTimelineValue(-120, 120, { phase: 'enter' });
  const xHold = useTimelineValue(120, 120, { phase: 'hold' });
  const xExit = useTimelineValue(120, 240, { phase: 'exit' });
  const opacity = useTimelineValue(0, 1, { phase: 'enter' });
  const opacityExit = useTimelineValue(1, 0, { phase: 'exit' });
  const hue = useTimelineValue(210, 340, { phase: 'hold' });

  // Only render the visible dot when the Loupe panel is actively
  // showing the demo scene. If the user has other scenes, Loupe
  // auto-switches away and this component stays invisible — no more
  // dot in the corner while you're working on real things.
  const isActive = registry?.activeSceneId === 'loupe-demo';
  if (!isActive) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <span
        style={{
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.8,
          padding: '4px 8px',
          borderRadius: 999,
          background: 'rgba(17, 24, 39, 0.85)',
          color: 'white',
          textTransform: 'uppercase',
        }}
      >
        Loupe Demo
      </span>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: \`hsl(\${hue.get()}, 85%, 58%)\`,
          transform: \`translateX(\${x.get() + xHold.get() - 120 + xExit.get() - 120}px)\`,
          opacity: opacity.get() * opacityExit.get(),
          transition: 'background 120ms linear',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
}
`;
